import { useEffect, useMemo, useRef, useState } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import {
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import {
  CreatePlanDialog,
  type CreatePlanDialogInput,
} from '~/components/planSubmit/CreatePlanDialog'
import { PreviousDiffBar } from '~/components/planSubmit/PreviousDiffBar'
import { useCreatePlanDialogCandidates } from '~/hooks/planSubmit/useCreatePlanDialogCandidates'
import { useCreateProcurementPlan } from '~/hooks/planSubmit/useCreateProcurementPlan'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { now as serverNow } from '~/models/clock'
import { fromProtoDate } from '~/models/dateTime'
import { KOMA_TIME_LABELS } from '~/models/komaToTime'
import { createdPlanNavigationState } from '~/models/planSubmission/createdPlanNavigation'
import { WORKFLOW_ID_PARAM } from '~/models/workflowIdParam'
import { DetailFetchError } from '~/pages/ProcurementPlanDetail/components/DetailFetchError'
import {
  BG_ALL_SELECTION,
  DetailFilterBar,
} from '~/pages/ProcurementPlanDetail/components/DetailFilterBar'
import { DetailHeader } from '~/pages/ProcurementPlanDetail/components/DetailHeader'
import { HandlingMemoPanel } from '~/pages/ProcurementPlanDetail/components/HandlingMemoPanel'
import { MismatchNavBanner } from '~/pages/ProcurementPlanDetail/components/MismatchNavBanner'
import { PlanTable } from '~/pages/ProcurementPlanDetail/components/PlanTable'
import { SpotTradeResultMismatchAlert } from '~/pages/ProcurementPlanDetail/components/SpotTradeResultMismatchAlert'
import { SubmitConfirmDialog } from '~/pages/ProcurementPlanDetail/components/SubmitConfirmDialog'
import { SubmitGuidanceAlert } from '~/pages/ProcurementPlanDetail/components/SubmitGuidanceAlert'
import { useDeeplinkHighlight } from '~/pages/ProcurementPlanDetail/hooks/useDeeplinkHighlight'
import { useDetailFetchFailure } from '~/pages/ProcurementPlanDetail/hooks/useDetailFetchFailure'
import { useGetPlanCounterpartyMismatch } from '~/pages/ProcurementPlanDetail/hooks/useGetPlanCounterpartyMismatch'
import { useGetProcurementPlanByFilter } from '~/pages/ProcurementPlanDetail/hooks/useGetProcurementPlanByFilter'
import { useGetProcurementPlanDetail } from '~/pages/ProcurementPlanDetail/hooks/useGetProcurementPlanDetail'
import { useMismatchNavigation } from '~/pages/ProcurementPlanDetail/hooks/useMismatchNavigation'
import { useRefetchWhenSubmitHoldEnds } from '~/pages/ProcurementPlanDetail/hooks/useRefetchWhenSubmitHoldEnds'
import { useSubmitProcurementPlan } from '~/pages/ProcurementPlanDetail/hooks/useSubmitProcurementPlan'
import {
  type SlotValue,
  calcBalanceDiffFlags,
  calcSupplyDemandBalance,
} from '~/pages/ProcurementPlanDetail/models/calc'
import {
  DIRECT_SUBMIT_HELD_REASON_KEY,
  type SubmitAvailability,
  resolveSubmitAvailability,
} from '~/pages/ProcurementPlanDetail/models/canSubmit'
import {
  type BreakdownColumn,
  aggregateColumnsByCounterparty,
  buildBreakdownColumns,
  shouldExcludeJepxMarket,
} from '~/pages/ProcurementPlanDetail/models/columns'
import {
  buildSubmitConfirmSummary,
  countChangedSlots,
} from '~/pages/ProcurementPlanDetail/models/confirmSummary'
import {
  buildSlotsMap,
  detailColumnTotals,
  detailPreviousBalances,
  slotsToValues,
} from '~/pages/ProcurementPlanDetail/models/detailTable'
import {
  DEFAULT_DISPLAY_MODE,
  type DisplayMode,
} from '~/pages/ProcurementPlanDetail/models/displayMode'
import { buildFilterInitialValues } from '~/pages/ProcurementPlanDetail/models/filterState'
import { hasEmptySlots } from '~/pages/ProcurementPlanDetail/models/hasEmptySlots'
import { isCounterpartyMismatchResolved } from '~/pages/ProcurementPlanDetail/models/hasUnresolvedMismatch'
import { resolveInternalInconsistency } from '~/pages/ProcurementPlanDetail/models/internalInconsistency'
import { internalInconsistencyLegendItems } from '~/pages/ProcurementPlanDetail/models/internalInconsistencyLegend'
import { resolveNoSubmissionRecordVersionNotice } from '~/pages/ProcurementPlanDetail/models/noSubmissionRecordNotice'
import {
  type SubmitGuidanceAction,
  resolveSubmitGuidance,
} from '~/pages/ProcurementPlanDetail/models/submitGuidance'
import { routePaths } from '~/routes/routePaths'

/** フィルタバーがユーザー変更で保持する対象日・エリアの override。undefined は
 * 「detail から導出した初期値をそのまま使う」ことを意味する
 * (procurement-plan-detail-filter 設計書 §5: 独立した state source を持たない)。 */
type FilterOverride = { date: Date; area: AreaCode }

/**
 * PlanTable にのみ渡す表示用 props の束 (procurement-plan-retailer-breakdown
 * 設計書、AC-06/07/08/09)。BG 全体選択時は既存 BG レベル値をそのまま束ね、
 * 事業者選択時は選択事業者の内訳に完全に差し替える (= フィールドごとの三項分岐
 * ではなく、この束単位で 1 回だけ分岐する)。提出確認ダイアログ・未入力警告
 * (hasEmptySlots) は本 view に依らず常に BG レベル値を直接参照する (確認画面の鉄則:
 * 提出対象は常に BG 全体)。
 */
type PlanTableView = {
  demandSlots: SlotValue[]
  /**
   * 需要計画値セルの hover tooltip 用「前回値」48 コマ。BG 全体表示のみ前回提出値を
   * 渡し、事業者選択時は全コマ undefined を渡す (AC-09 の裏側: 事業者選択中は
   * 「今回 = 当該小売の需要値 / 前回 = BG 全体の需要値」の比較不能な組を
   * 抑止する。demandDiffFlags 全 false と同一経路で扱い、tooltip も出さない)。
   */
  demandPreviousSlots: SlotValue[]
  procurementColumns: BreakdownColumn[]
  salesColumns: BreakdownColumn[]
  procurementTotals: SlotValue[]
  salesTotals: SlotValue[]
  balances: SlotValue[]
  demandDiffFlags: boolean[]
  /** 需給バランス列の前回差分フラグ (AC-PPD-020)。事業者選択中は全 false (AC-PPD-021-1)。 */
  balanceDiffFlags: boolean[]
}

// detail が無い間はヘッダーも誘導メッセージも描画しないので、 値を読む場面は無い。
const NO_DETAIL_SUBMIT_AVAILABILITY: SubmitAvailability = {
  enabled: false,
  disabledReason: undefined,
  disabledReasonIsDataDefect: false,
}
export const ProcurementPlanDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const planId = id ?? ''

  // workflow-back-bar-readonly AC-11/AC-22: 一覧から引き継いだ caseId で
  // 完了済みケースなら提出ボタンを read-only 化する
  // (fail-closed の理由は useCurrentWorkflowCase の JSDoc 参照)。
  const { caseId, isReadonly, isDateLocked, lockedTargetDate } =
    useCurrentWorkflowCase()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()

  const [isSubmitDialogOpen, setSubmitDialogOpen] = useState(false)
  const [isRecreateDialogOpen, setRecreateDialogOpen] = useState(false)
  // 誘導メッセージのボタンのスクロール先 (AC-PLC-020-2): 「不整合を確認」 はテーブル上部の
  // 不整合バナー (無ければテーブル)、「修正して再提出」 はテーブル。バナーの開き直しを
  // 描画してから動かすため、要求を state に置いて effect で処理する。
  const mismatchAnchorRef = useRef<HTMLDivElement>(null)
  const tableAnchorRef = useRef<HTMLDivElement>(null)
  const [scrollTarget, setScrollTarget] = useState<
    'mismatch' | 'table' | undefined
  >(undefined)
  useEffect(() => {
    if (scrollTarget === undefined) return
    const target =
      (scrollTarget === 'mismatch' ? mismatchAnchorRef.current : null) ??
      tableAnchorRef.current
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setScrollTarget(undefined)
  }, [scrollTarget])

  // ---- データ取得 ----
  const { data, error, errorUpdatedAt, isLoading, isFetching, refetch } =
    useGetProcurementPlanDetail(planId)
  // AC-02/AC-03: 初回取得失敗 (成功後の背景 refetch 失敗は含めない) と NotFound の判定。
  const { hasFailed: showFetchError, isNotFound } = useDetailFetchFailure({
    data,
    error,
    errorUpdatedAt,
  })
  const detail = data?.detail
  // AC-07: retailer_breakdowns（小売内訳）の供給源。空配列 = roster 無し
  // (生成経路・旧取込分) を意味し、DetailFilterBar 側で切替を disabled にする
  // (procurement-plan-retailer-breakdown 設計書 §5、旧 `data.retailers` /
  // RetailerInfo は deprecated のため非使用)。`?? []` の fallback がレンダー毎に
  // 新しい配列を作るため、下流 useMemo の依存を安定させるために useMemo 化する。
  const retailers = useMemo(() => detail?.retailerBreakdowns ?? [], [detail])
  const { submit, isSubmitting } = useSubmitProcurementPlan(planId)
  // AC-PPD-029: 計画が eBalance に提出記録の無い不整合通知を表示しているか。対応メモ
  // パネルと同じ read (同じ query key なので取得は 1 回)。取得失敗は案内を出さないだけ。
  const counterpartyMismatch = useGetPlanCounterpartyMismatch({
    planSubmissionId: planId,
  })
  // 版の状態がポーリングで変わったら (例: 提出済 → 受領済で 078 の通知が外れる)
  // 案内のフラグを読み直す。初回描画は上の query の取得に任せる。
  const detailStatus = data?.detail?.status
  const previousDetailStatus = useRef(detailStatus)
  const { refetch: refetchCounterpartyMismatch } = counterpartyMismatch
  useEffect(() => {
    const previous = previousDetailStatus.current
    previousDetailStatus.current = detailStatus
    if (previous === undefined || previous === detailStatus) return
    void refetchCounterpartyMismatch()
  }, [detailStatus, refetchCounterpartyMismatch])

  // ---- フィルタバー (procurement-plan-detail-filter 設計書 §5) ----
  // 対象日・エリアは detail から導出する独立していない state。ユーザーが
  // 変更した値は filterOverride に一時保持し、GetProcurementPlanByFilter が
  // 返した新しい計画の id へ navigate した後 (= planId 変化) にクリアする。
  const [filterOverride, setFilterOverride] = useState<
    FilterOverride | undefined
  >(undefined)
  // BG/小売切替はページ内 state のみ (API 呼び出しなし、設計書 §5)。初期表示は
  // BG 全体 (AC-02)。
  const [retailerSelection, setRetailerSelection] =
    useState<string>(BG_ALL_SELECTION)
  // 表示モード (取引先毎合計 / 取引毎) もページ内 state のみ (design #sd-view-seg)。
  // 既定は取引先毎合計 (DEFAULT_DISPLAY_MODE、AC-12)。
  const [displayMode, setDisplayMode] =
    useState<DisplayMode>(DEFAULT_DISPLAY_MODE)

  useEffect(() => {
    // 新しい計画に切り替わったら override をクリアし、新 detail の値を正とする。
    // 提出ダイアログも閉じる (フィルタ検索中に開いたダイアログを新計画へ持ち越さない)。
    setFilterOverride(undefined)
    setRetailerSelection(BG_ALL_SELECTION)
    setDisplayMode(DEFAULT_DISPLAY_MODE)
    setSubmitDialogOpen(false)
  }, [planId])

  const initialFilter = useMemo(
    () => buildFilterInitialValues(detail),
    [detail],
  )
  const filterDate = filterOverride?.date ?? initialFilter?.date ?? serverNow()
  const filterArea =
    filterOverride?.area ?? initialFilter?.area ?? AreaCode.UNSPECIFIED
  // AC-PLC-019: エリアの選択肢は対象 10 エリア ∩ 業務エリア設定。取得中・取得失敗は
  // 10 エリアのまま (fail-open)。業務エリアは表示の絞り込みだけで、表示中の計画の
  // エリアが業務エリアの外でも計画はそのまま表示する (conventions.md 業務エリアフィルタの原則)。
  const { selectableAreaCodes } = useBusinessAreaFilter(AREA_CODE_OPTIONS)

  const byFilterEnabled =
    filterOverride !== undefined && initialFilter !== undefined

  const { data: filterData, error: filterError } =
    useGetProcurementPlanByFilter({
      targetPeriodStartDate: filterDate,
      areaCode: filterArea,
      bgCode: initialFilter?.bgCode ?? '',
      timeframe: initialFilter?.lookupTimeframe,
      enabled: byFilterEnabled,
    })

  // AC-06: 該当計画なし (NOT_FOUND) の判定。フィルタ変更を試みていない時は false。
  const isFilterNotFound =
    byFilterEnabled &&
    filterError instanceof ConnectError &&
    filterError.code === Code.NotFound

  const isFilterError =
    byFilterEnabled && filterError !== null && !isFilterNotFound

  // AC-PPD-022 / 022-1 / AC-PPP-004: 提出管理の取引先カードから不整合範囲付きで
  // 開かれた時、計画表を表示できた最初の 1 回だけ該当セルを一時強調する。
  const deeplinkHighlight = useDeeplinkHighlight({
    isReady: detail !== undefined && !showFetchError,
    planId,
  })

  useEffect(() => {
    const newId = filterData?.detail?.id
    if (newId !== undefined && newId !== '' && newId !== planId) {
      void navigate(routePaths.planProcurementDetail.path.replace(':id', newId))
    }
  }, [filterData, planId, navigate])

  // フィルタ検索の開始時に提出ダイアログを閉じる (検索結果が旧計画の表示を
  // 置き換えるため、開いたままだと旧計画への提出手段が残る。PR #3013 レビュー指摘)。
  const handleDateChange = (date: Date): void => {
    setSubmitDialogOpen(false)
    setFilterOverride({ date, area: filterArea })
  }
  const handleAreaChange = (area: AreaCode): void => {
    setSubmitDialogOpen(false)
    setFilterOverride({ date: filterDate, area })
  }

  // ---- 前回差分 ----
  const previousSubmitted = detail?.previousSubmitted
  const hasPreviousSubmitted = previousSubmitted !== undefined

  // ---- proto detail から表示用スロット配列を導出（read-only） ----
  // 編集バッファ廃止: 計画値の編集は CreateProcurementPlan API が担う (AC-044)。
  // 本画面は受信した計画値をそのまま表示するに留める。

  const demandSlots: SlotValue[] = useMemo(
    () =>
      detail
        ? slotsToValues(detail.demandSlots)
        : Array<SlotValue>(48).fill(undefined),
    [detail],
  )

  const procurementSlotsMap: Map<string, SlotValue[]> = useMemo(
    () =>
      detail
        ? buildSlotsMap(detail.procurementValues, 'procurement')
        : new Map<string, SlotValue[]>(),
    [detail],
  )

  const salesSlotsMap: Map<string, SlotValue[]> = useMemo(
    () =>
      detail
        ? buildSlotsMap(detail.salesValues, 'sales')
        : new Map<string, SlotValue[]>(),
    [detail],
  )

  const prevDemandSlots = useMemo(
    () =>
      hasPreviousSubmitted
        ? slotsToValues(previousSubmitted.demandSlots)
        : Array<SlotValue>(48).fill(undefined),
    [hasPreviousSubmitted, previousSubmitted],
  )

  const prevProcurementMap = useMemo(
    () =>
      hasPreviousSubmitted
        ? buildSlotsMap(previousSubmitted.procurementValues, 'procurement')
        : new Map<string, SlotValue[]>(),
    [hasPreviousSubmitted, previousSubmitted],
  )

  const prevSalesMap = useMemo(
    () =>
      hasPreviousSubmitted
        ? buildSlotsMap(previousSubmitted.salesValues, 'sales')
        : new Map<string, SlotValue[]>(),
    [hasPreviousSubmitted, previousSubmitted],
  )

  const demandDiffFlags = useMemo(
    () =>
      hasPreviousSubmitted
        ? demandSlots.map((v, i) => v !== prevDemandSlots[i])
        : Array<boolean>(48).fill(false),
    [hasPreviousSubmitted, demandSlots, prevDemandSlots],
  )

  // ---- BreakdownColumn 配列構築 ----
  // 列はすべて値行（計画作成時に選択した需要調達計画マスタの取引先由来）から導出する。
  // JEPX 市場列も常時固定表示せず、値行に当該 sourceType がある時だけ出す
  // （models/columns.ts に純関数化）。沖縄の JEPX 対象外はマスタに JEPX 取引先を
  // 登録しないことで表現し、表示層に沖縄特例を持たない。
  const procurementColumns = useMemo(
    (): BreakdownColumn[] =>
      detail
        ? buildBreakdownColumns({
            rows: detail.procurementValues,
            side: 'procurement',
            slotsMap: procurementSlotsMap,
            prevMap: prevProcurementMap,
            hasPrev: hasPreviousSubmitted,
            translate: t,
          })
        : [],
    [detail, procurementSlotsMap, prevProcurementMap, hasPreviousSubmitted, t],
  )

  const salesColumns = useMemo(
    (): BreakdownColumn[] =>
      detail
        ? buildBreakdownColumns({
            rows: detail.salesValues,
            side: 'sales',
            slotsMap: salesSlotsMap,
            prevMap: prevSalesMap,
            hasPrev: hasPreviousSubmitted,
            translate: t,
          })
        : [],
    [detail, salesSlotsMap, prevSalesMap, hasPreviousSubmitted, t],
  )

  // ---- 合計・需給バランス算出（表示列から導出。F-1: 表示と集計を同一 source に）----
  const procurementTotals = useMemo(
    () => detailColumnTotals(procurementColumns),
    [procurementColumns],
  )

  const salesTotals = useMemo(
    () => detailColumnTotals(salesColumns),
    [salesColumns],
  )

  const balances = useMemo(
    () =>
      Array.from({ length: 48 }, (_, i) =>
        calcSupplyDemandBalance(
          procurementTotals[i],
          demandSlots[i],
          salesTotals[i],
        ),
      ),
    [procurementTotals, demandSlots, salesTotals],
  )

  // AC-PPD-020: 需給バランス列も前回差分の対象。前回提出が無ければ差分にしない (AC-PPD-021)。
  const balanceDiffFlags = useMemo(
    () =>
      hasPreviousSubmitted
        ? calcBalanceDiffFlags(
            balances,
            detailPreviousBalances(
              prevProcurementMap,
              prevDemandSlots,
              prevSalesMap,
            ),
          )
        : Array<boolean>(48).fill(false),
    [
      hasPreviousSubmitted,
      balances,
      prevProcurementMap,
      prevDemandSlots,
      prevSalesMap,
    ],
  )

  // ---- 提出フロー（AC-01/AC-02） ----
  const submitConfirmSummary = useMemo(
    () =>
      buildSubmitConfirmSummary({
        planTypeLabel: t(
          'page:procurementPlanDetail.submitConfirm.planTypeValue',
        ),
        targetPeriodStartDate: detail?.targetPeriodStartDate,
        areaLabel:
          detail !== undefined ? convertAreaLabel(detail.areaCode) : '',
        procurementTotals,
        demandSlots,
        salesTotals,
        balances,
        // ユーザー決定（2026-10-14）: 表示モードに依らず取引先毎合計で数える。
        // 契約を足しただけで変更コマ数が増えないようにするため（#9998）。
        changedSlotCount: countChangedSlots(
          demandDiffFlags,
          aggregateColumnsByCounterparty(procurementColumns),
          aggregateColumnsByCounterparty(salesColumns),
        ),
      }),
    [
      t,
      detail,
      procurementTotals,
      demandSlots,
      salesTotals,
      balances,
      demandDiffFlags,
      procurementColumns,
      salesColumns,
    ],
  )

  // AC-PPD-024 / 025: 計画内不整合のコマの強調と凡例。判定は backend が表示中の版の値で行った
  // 結果 (一覧・ヘッダーと同じ規則)。ここでは種別「BG」の表示か、単独 BG で事業者を選んでいる
  // 時だけ出す (合同 BG で事業者を選んでいる時は代表事業者を含めて出さない)。
  const internalInconsistency = useMemo(
    () =>
      detail === undefined
        ? undefined
        : resolveInternalInconsistency({
            detail,
            isBgView: retailerSelection === BG_ALL_SELECTION,
            retailerCount: retailers.length,
          }),
    [detail, retailerSelection, retailers.length],
  )

  // 凡例は前回差分の項目と計画内不整合の項目を 1 本の帯に並べる。
  const legendItems = useMemo(
    () => internalInconsistencyLegendItems(internalInconsistency, t),
    [internalInconsistency, t],
  )
  const showsPreviousDiffLegend =
    hasPreviousSubmitted && retailerSelection === BG_ALL_SELECTION

  // ---- BG/小売切替 (procurement-plan-retailer-breakdown 設計書、AC-06/07/08/09) ----
  // PlanTable の表示用 props を 1 束にまとめ、BG 全体 / 事業者選択で先頭 1 回だけ
  // 分岐する (= フィールドごとに三項を反復しない)。
  const tableView: PlanTableView = useMemo(() => {
    if (retailerSelection === BG_ALL_SELECTION) {
      return {
        demandSlots,
        demandPreviousSlots: prevDemandSlots,
        procurementColumns,
        salesColumns,
        procurementTotals,
        salesTotals,
        balances,
        demandDiffFlags,
        balanceDiffFlags,
      }
    }

    // 選択事業者 (retailer_code 一致)。見つからない (stale selection) 場合は
    // 空セクション扱いとする (BG データへのフォールバックは選択中の表示と矛盾
    // するため避ける)。
    const retailer = retailers.find((r) => r.retailerCode === retailerSelection)
    const retailerDemandSlots = slotsToValues(retailer?.demandSlots ?? [])
    const retailerProcurementRows = retailer?.procurementValues ?? []
    const retailerSalesRows = retailer?.salesValues ?? []
    // AC-PPD-026: 合同 BG で代表事業者以外を選んでいる時は JEPX 市場列を出さない
    // (合計・需給バランスにも入れない)。
    const excludeJepxMarket = shouldExcludeJepxMarket(
      retailers,
      retailerSelection,
    )

    // AC-PPD-006-1: 内訳セクションが空 (retailer の procurement/sales_values が空) でも
    // read-only 詳細画面では「取引なし = 0」なので detailColumnTotals が合計を 0 とし
    // (0 ではなく "−" にしない)、需給バランスも需要から算出される。
    const retailerProcurementColumns = buildBreakdownColumns({
      rows: retailerProcurementRows,
      side: 'procurement',
      slotsMap: buildSlotsMap(retailerProcurementRows, 'procurement'),
      prevMap: prevProcurementMap,
      hasPrev: false, // AC-09: 事業者選択中は前回差分ハイライトを適用しない
      translate: t,
      excludeJepxMarket,
    })
    const retailerSalesColumns = buildBreakdownColumns({
      rows: retailerSalesRows,
      side: 'sales',
      slotsMap: buildSlotsMap(retailerSalesRows, 'sales'),
      prevMap: prevSalesMap,
      hasPrev: false, // AC-09
      translate: t,
      excludeJepxMarket,
    })
    const retailerProcurementTotals = detailColumnTotals(
      retailerProcurementColumns,
    )
    const retailerSalesTotals = detailColumnTotals(retailerSalesColumns)
    const retailerBalances = Array.from({ length: 48 }, (_, i) =>
      calcSupplyDemandBalance(
        retailerProcurementTotals[i],
        retailerDemandSlots[i],
        retailerSalesTotals[i],
      ),
    )

    return {
      demandSlots: retailerDemandSlots,
      // AC-09: 事業者選択中は「今回 = 当該小売の需要値 / 前回 = BG 全体の需要値」
      // という比較不能な組になるため、需要側の前回値も tooltip 抑止用に全 undefined
      // へ差し替える (demandDiffFlags 全 false と同一の抑止経路)。
      demandPreviousSlots: Array<SlotValue>(48).fill(undefined),
      procurementColumns: retailerProcurementColumns,
      salesColumns: retailerSalesColumns,
      procurementTotals: retailerProcurementTotals,
      salesTotals: retailerSalesTotals,
      balances: retailerBalances,
      demandDiffFlags: Array<boolean>(48).fill(false),
      balanceDiffFlags: Array<boolean>(48).fill(false),
    }
  }, [
    retailerSelection,
    retailers,
    demandSlots,
    procurementColumns,
    salesColumns,
    procurementTotals,
    salesTotals,
    balances,
    demandDiffFlags,
    balanceDiffFlags,
    prevDemandSlots,
    prevProcurementMap,
    prevSalesMap,
    t,
  ])

  // AC-PPD-009〜017 / 023 / 029: 未解消の取引相手間ミスマッチの強調と不整合バナー。位置は
  // 通知 (`partners[]`)、照合先は表示中の列 (種別選択後。版の値の列) から決める。
  const mismatchNav = useMismatchNavigation({
    mismatch: counterpartyMismatch.data,
    procurementColumns: tableView.procurementColumns,
    salesColumns: tableView.salesColumns,
    planIdentity: planId,
  })

  const directSubmitHeldUntil =
    detail?.directSubmitHeldUntil !== undefined
      ? timestampDate(detail.directSubmitHeldUntil)
      : undefined
  // AC-PLC-062: 開いたまま前日 17:00 を迎えたら読み直し、 提出を押せる状態に戻す
  useRefetchWhenSubmitHoldEnds(directSubmitHeldUntil, refetch)
  const submitAvailability =
    detail !== undefined
      ? resolveSubmitAvailability({
          status: detail.status,
          isSubmitting,
          hasEmptySlots: hasEmptySlots(demandSlots),
          isReadonly,
          submitBlockReason: detail.submitBlockReason,
          directSubmitHeldUntil,
        })
      : NO_DETAIL_SUBMIT_AVAILABILITY
  // 「計画を作り直す」 を押せるか (AC-PLC-020-2 の誘導と AC-PPP-021 の食い違いのバナーで共通)
  const isRecreateEnabled =
    !isReadonly && detail?.targetPeriodStartDate !== undefined
  const submitGuidance =
    detail !== undefined
      ? resolveSubmitGuidance({
          submitBlockReason: detail.submitBlockReason,
          latestPlanSubmissionId: detail.latestPlanSubmissionId,
          status: detail.status,
          errorCategory: detail.errorCategory,
          breakdownRowLimit: detail.breakdownRowLimit,
          credentialRejectionResultCodes: detail.credentialRejectionResultCodes,
          isSubmitActionEnabled: submitAvailability.enabled,
          submitDisabledReason: submitAvailability.disabledReason,
          isCounterpartyMismatchResolved: isCounterpartyMismatchResolved(
            counterpartyMismatch.data,
          ),
          isRecreateEnabled,
        })
      : undefined
  const noSubmissionRecordNotice =
    detail !== undefined
      ? resolveNoSubmissionRecordVersionNotice({
          showsNoSubmissionRecordNotice:
            counterpartyMismatch.data?.showsNoSubmissionRecordNotice === true,
          status: detail.status,
          submitBlockReason: detail.submitBlockReason,
          errorCategory: detail.errorCategory,
        })
      : undefined
  // 誘導メッセージのボタン (AC-PLC-020-2 / 055)。
  const handleGuidanceAction = (action: SubmitGuidanceAction): void => {
    switch (action.kind) {
      case 'submit':
        // 提出ボタンと同じ提出確認ダイアログ (AC-PLC-029)
        setSubmitDialogOpen(true)
        return
      case 'scrollToTable':
        setScrollTarget('table')
        return
      case 'showMismatch':
        // 閉じたバナーも開き直してから (再描画後に) テーブル上部のバナーへスクロールする
        mismatchNav.reopen()
        setScrollTarget('mismatch')
        return
      case 'recreate':
        setRecreateDialogOpen(true)
        return
      case 'openLatest':
        void navigate(
          routePaths.planProcurementDetail.path.replace(':id', action.planId),
        )
        return
    }
  }

  // 「計画を作り直す」 (AC-PLC-020-2 作成失敗、AC-PLC-056 上記以外〈MISSING_BASELINE_BOXES〉、
  // AC-PPP-021 約定との食い違い): 計画作成ダイアログ (AC-PLC-038) を当該計画の対象日・エリア・BG を
  // 初期値にして開く (AC-PLC-040 の初期値に代える)。
  // 候補データはボタンを出している時だけ取得し、取得失敗で詳細全体を
  // error boundary に飛ばさない (ダイアログの「作成」 が押せないままになる)。
  // 食い違いの有無は backend が計画単位で返す値をそのまま使う（版の判定はしない）。
  const showsSpotTradeResultMismatch = detail?.spotTradeResultMismatch === true
  const isRecreatable =
    submitGuidance?.action?.kind === 'recreate' || showsSpotTradeResultMismatch
  const createDialogCandidates = useCreatePlanDialogCandidates({
    enabled: isRecreatable,
    throwOnError: false,
  })
  const { mutate: createPlan, isPending: isCreating } =
    useCreateProcurementPlan()
  const recreateInitialSelection = useMemo(
    () =>
      detail === undefined
        ? undefined
        : { areaCode: detail.areaCode, bgCode: detail.bgCode },
    [detail],
  )
  const recreateInitialTargetDate = useMemo(
    () =>
      detail?.targetPeriodStartDate === undefined
        ? undefined
        : fromProtoDate(detail.targetPeriodStartDate),
    [detail],
  )
  // 作成完了後は一覧へ遷移し、一覧が対象日を作成した計画の対象日に合わせる
  // (AC-PLC-054)。失敗した版は残る (AC-PLC-045)。失敗時はダイアログを開いたまま。
  const handleRecreate = (input: CreatePlanDialogInput): void => {
    createPlan(input, {
      onSuccess: () => {
        setRecreateDialogOpen(false)
        showSuccessToast({
          title: t('page:procurementPlanList.createButton.successTitle'),
          description: t(
            'page:procurementPlanList.createButton.successDescription',
          ),
        })
        void navigate(
          caseId === undefined
            ? routePaths.planProcurement.path
            : `${routePaths.planProcurement.path}?${WORKFLOW_ID_PARAM}=${encodeURIComponent(caseId)}`,
          { state: createdPlanNavigationState(input.targetPeriodStartDate) },
        )
      },
      onError: () => {
        showErrorToast({
          title: t('page:procurementPlanList.createButton.errorTitle'),
        })
      },
    })
  }

  const handleSubmitClick = (): void => {
    setSubmitDialogOpen(true)
  }

  // AC-02/AC-03: 初回取得失敗時の再読み込み導線。Code.NotFound を含む全 isError を
  // 単一経路で扱う (design §4 主要意思決定「Boundary / Error」)。
  const handleReloadClick = (): void => {
    void refetch()
  }

  const handleConfirmSubmit = (): void => {
    submit()
    setSubmitDialogOpen(false)
  }

  const breadcrumbItems = [
    {
      label: t('common:nav.plan'),
      to: routePaths.planProcurement.path,
    },
    {
      label: t('common:route.planProcurement'),
      to: routePaths.planProcurement.path,
    },
    {
      // パンくず末尾は画面名（design 準拠）。BG コード等は status block に表示。
      label: t('common:route.planProcurementDetail'),
      bold: true,
    },
  ]

  // ページ最上部の通知 (alert-placement.md §0・§2。業務が止まる → 対応が要る → 知っておけばよい の順)。
  // 初回取得失敗の時は取得失敗だけを出し、ヘッダー・表と同じく、取得前・フィルタの空状態/エラーでは出さない。
  // AC-02/AC-03: 初回取得失敗 (Code.NotFound 含む全 error を単一経路で扱う。design §4「Boundary / Error」)。
  // DetailHeader / DetailFilterBar / PlanTable は非描画のまま、取得失敗の Compact Alert (+ 再読み込み) だけを
  // `notices` に出す (本文は何も描画しない)。条件は `hasNeverSucceeded && hasEverFailed` で初回取得失敗のみに限定する
  // (成功後の背景 refetch 失敗で表示済みの table 等を置換しない)。refetch 中は React Query が error を null にするが、
  // 再読み込みボタンは `loading={isFetching}` で pending 中に disabled になる (Alert は残す)。
  // NotFound は恒久エラー (BFF が対象組織外・kind 不一致を NotFound でマスクする) のため、通信・サーバーエラーとは
  // 文言を分ける。UI 経路は単一のまま (再読み込みは refetch が同じ NotFound を返すだけの安全な no-op)。
  const pageNotices = showFetchError ? (
    <DetailFetchError
      isNotFound={isNotFound}
      isReloading={isFetching}
      onReload={handleReloadClick}
    />
  ) : isLoading || !detail || isFilterNotFound || isFilterError ? undefined : (
    <>
      {/* AC-PLC-062: 提出を止める理由が OCCTO の受付時間外の時の案内 (React mock の notices と同じ info) */}
      {submitAvailability.disabledReason?.key ===
        DIRECT_SUBMIT_HELD_REASON_KEY && (
        <CompactAlert
          status="info"
          title={t(
            submitAvailability.disabledReason.key,
            submitAvailability.disabledReason.values,
          )}
        />
      )}
      {/* AC-PLC-020-2 の位置: 提出を止める理由 (AC-PLC-055 / 056) か送信失敗の原因別の誘導メッセージ */}
      {submitGuidance !== undefined && (
        <SubmitGuidanceAlert
          guidance={submitGuidance}
          onAction={handleGuidanceAction}
        />
      )}

      {/* AC-PPP-020: 提出後のスポット約定との食い違い。
      計画が変わったら本文の時間帯を選び直す */}
      {showsSpotTradeResultMismatch && (
        <SpotTradeResultMismatchAlert
          key={detail.id}
          onRecreate={() => {
            setRecreateDialogOpen(true)
          }}
          recreateDisabled={!isRecreateEnabled}
          targetDate={recreateInitialTargetDate}
        />
      )}

      {/* AC-PPD-029: 広域機関に eBalance に提出記録の無い計画がある時の案内 */}
      {noSubmissionRecordNotice !== undefined && (
        <CompactAlert
          description={
            noSubmissionRecordNotice.showsReplaceNote
              ? t(
                  'page:procurementPlanDetail.noSubmissionRecord.versionReplaceNote',
                )
              : undefined
          }
          status="warning"
          title={t(
            'page:procurementPlanDetail.noSubmissionRecord.versionNotice',
          )}
        />
      )}

      {/* AC-PPP-019: スポットの約定結果が未着の札がある時の案内 (ボタンなし。提出は止めない) */}
      {detail.bidsAwaitingMatchResultCount > 0 && (
        <CompactAlert
          description={t('page:planSubmission.awaitingMatchResult.body', {
            pendingCount: detail.bidsAwaitingMatchResultCount,
          })}
          status="info"
          title={t('page:planSubmission.awaitingMatchResult.title')}
        />
      )}
    </>
  )

  return (
    <WorkflowPageLayout breadcrumbItems={breadcrumbItems} notices={pageNotices}>
      <PageBody>
        {showFetchError ? null : (
          <RegionLoading
            flexDirection="column"
            flex="1"
            gap={4}
            display="flex"
            minHeight={isLoading || !detail ? '200px' : 0}
            isLoading={isLoading || !detail}
          >
            {isLoading || !detail ? null : (
              <>
                {/* フィルタバー (対象日 + エリア + BG/小売切替、procurement-plan-detail-filter 設計書) */}
                <DetailFilterBar
                  area={filterArea}
                  caseTargetDate={lockedTargetDate}
                  date={filterDate}
                  displayMode={displayMode}
                  isDateLocked={isDateLocked}
                  onAreaChange={handleAreaChange}
                  onDateChange={handleDateChange}
                  onDisplayModeChange={setDisplayMode}
                  onRetailerChange={setRetailerSelection}
                  retailers={retailers}
                  selectableAreaCodes={selectableAreaCodes}
                  selectedRetailer={retailerSelection}
                />

                {isFilterNotFound || isFilterError ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: LightModeSemanticColors.Text.midEmphasis.value,
                      fontSize: fontSizes.md.value,
                    }}
                  >
                    {isFilterNotFound
                      ? t('page:procurementPlanDetail.filter.noMatch')
                      : t('page:procurementPlanDetail.error.loadFailed')}
                  </div>
                ) : (
                  <>
                    {/* ヘッダー情報 (status block, design ds-submit-meta 相当。最上部) */}
                    {/* defer: sender_code は proto 未提供のため非表示 */}
                    <DetailHeader
                      bgCode={detail.bgCode}
                      bgName={detail.bgName}
                      breakdownRowLimit={detail.breakdownRowLimit}
                      creationAlignedKomaNumbers={
                        detail.creationAlignedKomaNumbers
                      }
                      errorCategory={detail.errorCategory}
                      excludedKomaNumbers={detail.excludedKomaNumbers}
                      fileCreatedAt={detail.fileCreatedAt}
                      isSubmitting={isSubmitting}
                      onSubmit={handleSubmitClick}
                      previousSubmittedAt={previousSubmitted?.submittedAt}
                      receiptCheckSuspendedByCallLimit={
                        detail.receiptCheckSuspendedByCallLimit
                      }
                      receptionErrors={detail.receptionErrors}
                      sendRetrying={detail.sendRetrying}
                      status={detail.status}
                      submissionDeadlineAt={detail.submissionDeadlineAt}
                      submitAvailability={submitAvailability}
                      submitBlockReason={detail.submitBlockReason}
                      submittedAt={detail.submittedAt}
                      submittedBy={detail.submittedBy}
                    />

                    {/* 対応メモパネル (AC-PSM-068: 入力欄と履歴を解消ボタンと同じ場所に置く。
                    解消ボタン〈AC-PSM-063〉はパネルの見出し行に置く。Des #9552)。
                    計画が変わったら入力中の本文・開閉・memo id を持ち越さない */}
                    <HandlingMemoPanel
                      key={planId}
                      target={{ planSubmissionId: planId }}
                    />

                    {/* 凡例の帯（design sd-legend）。前回提出からの変更箇所の項目は、前回提出があり、
                    かつ BG 全体表示中のみ（AC-PPD-021-1: 個別小売電気事業者選択中はセル色付き抑止
                    (index.tsx 内 tableView の BG_ALL_SELECTION 分岐) と同一条件で抑止し、抑止 seam を
                    一致させる）。計画内不整合の項目（AC-PPD-024 / 025）は legendItems があれば足す。
                    どちらの項目も無ければ帯を出さない */}
                    {(showsPreviousDiffLegend || legendItems.length > 0) && (
                      <PreviousDiffBar
                        changedLabelKey={
                          showsPreviousDiffLegend
                            ? 'page:procurementPlanDetail.previousDiff.changedLabel'
                            : undefined
                        }
                        extraItems={legendItems}
                      />
                    )}

                    {/* AC-PPD-013〜017: 不整合バナー。表のスクロール領域の外に置き、表をスクロール
                        しても見え続ける (issue #10317)。外側の要素は誘導メッセージの
                        「不整合を確認」 のスクロール先 */}
                    {mismatchNav.banner.items.length > 0 && (
                      <div ref={mismatchAnchorRef}>
                        <MismatchNavBanner {...mismatchNav.banner} />
                      </div>
                    )}

                    {/* 48 コマテーブル（design ecl-table-wrap: 枠線 + 角丸 8px + 白背景） */}
                    <PageScrollArea>
                      <div ref={tableAnchorRef} />
                      <PlanTable
                        balanceDiffFlags={tableView.balanceDiffFlags}
                        balances={tableView.balances}
                        deeplinkHighlight={deeplinkHighlight}
                        demandDiffFlags={tableView.demandDiffFlags}
                        demandPreviousSlots={tableView.demandPreviousSlots}
                        demandSlots={tableView.demandSlots}
                        displayMode={displayMode}
                        hasPreviousSubmitted={hasPreviousSubmitted}
                        internalInconsistency={internalInconsistency}
                        mismatch={mismatchNav.table}
                        procurementColumns={tableView.procurementColumns}
                        procurementTotals={tableView.procurementTotals}
                        salesColumns={tableView.salesColumns}
                        salesTotals={tableView.salesTotals}
                        timeLabels={KOMA_TIME_LABELS}
                      />
                    </PageScrollArea>
                  </>
                )}

                {/* 提出確認ダイアログ (AC-01/AC-02)。フィルタ検索の空状態・エラー時は
                レンダーしない (検索中に開いたダイアログから、表示されていない旧計画を
                提出できてしまう競合を塞ぐ。PR #3013 レビュー指摘)。 */}
                {/* 「計画を作り直す」 の計画作成ダイアログ (AC-PLC-020-2 / 038) */}
                {isRecreatable && recreateInitialTargetDate !== undefined && (
                  <CreatePlanDialog
                    bgMasterItems={createDialogCandidates.bgMasterItems}
                    bgMasterPeriods={createDialogCandidates.bgMasterPeriods}
                    bgNames={createDialogCandidates.bgNames}
                    businessAreas={createDialogCandidates.businessAreas}
                    initialSelection={recreateInitialSelection}
                    initialTargetDate={recreateInitialTargetDate}
                    isCandidateDataReady={
                      createDialogCandidates.isCandidateDataReady
                    }
                    isCreating={isCreating}
                    isOpen={isRecreateDialogOpen}
                    onClose={() => {
                      setRecreateDialogOpen(false)
                    }}
                    onCreate={handleRecreate}
                  />
                )}

                {!(isFilterNotFound || isFilterError) && (
                  <SubmitConfirmDialog
                    isOpen={isSubmitDialogOpen}
                    isSubmitting={isSubmitting}
                    onClose={() => {
                      setSubmitDialogOpen(false)
                    }}
                    onConfirm={handleConfirmSubmit}
                    summary={submitConfirmSummary}
                  />
                )}
              </>
            )}
          </RegionLoading>
        )}
      </PageBody>
    </WorkflowPageLayout>
  )
}
